import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ADMIN_PERMISSIONS } from './navigation';

/**
 * The session check on a cold load — the first thing anybody sees, and for a second or two
 * the only thing.
 *
 * It was a blank green square above a progress bar, which reads as a screen that has not
 * finished building rather than as a product opening. The name goes first because that is
 * what tells somebody they are in the right place; a logo on its own does not, least of all
 * on a shared machine.
 */
function FullPageLoader() {
  return (
    <div className="splash" role="status" aria-live="polite">
      <div className="splash__inner">
        <div className="wordmark">
          <b>Inditech</b> <span>Document Hub</span>
        </div>
        <div className="splash__mark" aria-hidden="true">iD</div>
        <div className="splash__rule" aria-hidden="true">
          <i />
        </div>
        <span className="splash__caption">Loading your workspace…</span>
      </div>
    </div>
  );
}

/**
 * Route guard. Repeats the sidebar's permission check so a typed URL cannot reach a
 * screen the user has no business seeing — and the API repeats it once more, decisively.
 */
export default function ProtectedRoute({ children, permission, permissions }) {
  const { status, can } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader />;
  if (status !== 'authenticated') return <Navigate to="/login" state={{ from: location }} replace />;

  const required = permissions || (permission ? [permission] : null);
  if (required && !required.some((p) => can(p))) return <Navigate to="/403" replace />;

  return children;
}

export function AdminRoute({ children }) {
  return <ProtectedRoute permissions={ADMIN_PERMISSIONS}>{children}</ProtectedRoute>;
}
